<template>
  <fullscreen-overlay-frame :title="title"
                            id="energy-tariff-dialog"
                            icon="assignment"
                            color="primary"
                            centered
                            closable
                            @close="close">

    <template v-slot:content>

      <!-- Name -->
      <v-text-field outlined
                    :label="$t('energy-tariff-dialog.label.name')"
                    v-model="tariff.tariffName"
                    class="mb-2"/>

      <!-- Price -->
      <v-text-field outlined
                    :label="$t('energy-tariff-dialog.label.price') + ' (' + $t('app.unit.cent') + ')'"
                    v-model.number="tariff.tariffValue"
                    type="number"
                    min="0"
                    :prefix="isRtl ? $t('app.unit.cent') : undefined"
                    :suffix="!isRtl ? $t('app.unit.cent') : undefined"
                    class="mb-2"/>


      <!-- Validity period header -->

      <content-card :title="$t('energy-tariff-dialog.label.validity-period')"
                    icon="update"
                    class="my-5">
        <template v-slot:content>
          <v-list class="py-0">

            <div v-for="(period, index) in tariff.activePeriods"
                 :key="period.id">
              <v-list-item>
                <!-- Time Row -->
                <v-row no-gutters>
                  <!-- start -->
                  <v-col cols="6">
                    <v-list-item dense two-line class="ma-0">
                      <v-list-item-icon class="my-auto">
                        <v-icon class="material-icons-outlined" color="primary">schedule</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="primary--text font-weight-bold">{{ period.start }}
                          {{ $t('app.unit.o-clock') }}
                        </v-list-item-title>
                        <v-list-item-subtitle>{{ $t('energy-tariff-dialog.label.valid-from') }}</v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-btn icon @click="toggleStartTime(period)">
                          <v-icon class="material-icons-outlined" color="primary">{{
                              period.isEditingStart ? 'check' : 'edit'
                            }}
                          </v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-col>

                  <v-divider vertical/>

                  <!-- End -->
                  <v-col cols="6">
                    <v-list-item dense two-line class="ma-0">
                      <v-list-item-icon class="my-auto">
                        <v-icon class="material-icons-outlined" color="primary">schedule</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="primary--text font-weight-bold">{{ period.end }}
                          {{ $t('app.unit.o-clock') }}
                        </v-list-item-title>
                        <v-list-item-subtitle>{{ $t('energy-tariff-dialog.label.valid-until') }}</v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-btn icon @click="toggleEndTime(period)">
                          <v-icon class="material-icons-outlined" color="primary">{{
                              period.isEditingEnd ? 'check' : 'edit'
                            }}
                          </v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-col>
                </v-row>

                <v-list-item-action>
                  <v-btn icon @click="deletePeriod(index)">
                    <v-icon class="material-icons-outlined" color="primary">delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>

              <v-list-item v-if="period.isEditingStart || period.isEditingEnd">
                <v-time-picker
                    v-if="period.isEditingStart"
                    class="elevation-0 mx-auto"
                    color="primary"
                    v-model="period.start"
                    format="24hr"
                    :allowed-minutes="timePickerOptions.allowedSteps"
                    no-title
                ></v-time-picker>
                <v-time-picker
                    v-if="period.isEditingEnd"
                    class="elevation-0 mx-auto"
                    color="primary"
                    v-model="period.end"
                    format="24hr"
                    :allowed-minutes="timePickerOptions.allowedSteps"
                    no-title
                ></v-time-picker>
              </v-list-item>
            </div>

            <!-- UncoveredTimePeriods -->

            <v-list-item v-if="uncoveredTimePeriods.length > 0">
              <v-list-item-content>
                <v-list-item-subtitle class="mb-2">
                  {{ $t('energy-tariff-dialog.label.uncovered-periods') }}
                </v-list-item-subtitle>
                <div>
                  <v-chip v-for="(period, index) in uncoveredTimePeriods"
                          v-bind:key="index"
                          @click="addPeriod(period.start, period.end)"
                          class="mx-1">
                    {{ minutesToTime(period.start) }}–{{ minutesToTime(period.end) }} {{ $t('app.unit.o-clock') }}
                  </v-chip>
                </div>
              </v-list-item-content>
            </v-list-item>

            <v-simple-table>
              <template v-slot:default>
                <tbody class="tileBackground">
                <tr>
                  <td colspan="4" class="primary--text font-weight-bold" @click="addPeriod()">
                    <v-icon color="primary">add</v-icon>
                    {{ $t('energy-tariff-dialog.button.add-period') }}
                  </td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>

          </v-list>
        </template>
      </content-card>

    </template>

    <template v-slot:actions>
      <v-btn large
             color="primary"
             class="font-weight-bold block-sm mx-auto d-block mb-3"
             :disabled="!tariff.tariffName || tariff.tariffValue === null || tariff.tariffValue === ''"
             @click="saveTariff">
        {{ $t('app.save') }}
      </v-btn>
      <v-btn depressed large outlined
             v-if="!isNew"
             color="primary"
             class="font-weight-bold block-sm mx-auto d-block mb-3"
             @click="deleteTariff(tariff)">
        <v-icon class="material-icons-outlined" left>delete</v-icon>
        {{ $t('energy-tariff-dialog.button.delete-tariff') }}
      </v-btn>
    </template>

  </fullscreen-overlay-frame>
</template>

<script>
import FullscreenOverlayFrame from "@/templates/dialogs/FullscreenOverlayFrame";
import ContentCard from "@/templates/components/ContentCard.vue";

export default {
  name: 'EnergyTariffDialog',

  components: {
    ContentCard,
    FullscreenOverlayFrame
  },

  props: ['data'],

  data: function () {
    return {
      isNew: true,
      periodIdCounter: 0,
      timePickerOptions: {
        allowedSteps: m => m % 15 === 0
      },
      tariff: {
        tariffName: '',
        tariffValue: null,
        activePeriods: []
      },
      additionalBlockedPeriods: [],
    }
  },

  watch: {
    'tariff.activePeriods': {
      deep: true,
      handler() {
        this.sortActivePeriods()
      }
    }
  },

  computed: {
    /**
     * Returns all time periods within a day (0–1440 minutes) that are not yet
     * covered by any period — neither from other tariffs (additionalBlockedPeriods)
     * nor from the tariff currently being created/edited (tariff.activePeriods).
     *
     * additionalBlockedPeriods: stored as numbers (minutes since midnight).
     * tariff.activePeriods: stored as time strings (e.g. "4:15") during the
     * dialog lifecycle and are converted to minutes here via timeToMinutes().
     *
     * Example:
     *   additionalBlockedPeriods = [{ start: 0, end: 15 }, { start: 60, end: 720 }]
     *   tariff.activePeriods     = [{ start: "0:30", end: "1:00" }, { start: "20:45", end: "21:30" }]
     *
     *   → returns [{ start: 15, end: 30 }, { start: 720, end: 1245 }, { start: 1290, end: 1440 }]
     *
     * @returns {{ start: number, end: number }[]} Array of uncovered time periods
     */
    uncoveredTimePeriods() {
      const allPeriods = [
        ...(this.additionalBlockedPeriods || []),
        ...this.tariff.activePeriods.map(p => ({
          start: this.timeToMinutes(p.start),
          end: this.timeToMinutes(p.end),
        }))
      ]
      return this.calculateUncoveredPeriods(allPeriods)
    },

    /**
     * Returns true if the current locale uses right-to-left text direction.
     */
    isRtl() {
      return this.$vuetify.rtl;
    },
    /**
     * Returns the dialog title depending on whether a new tariff is being created or an existing one edited.
     */
    title() {
      return this.isNew
          ? this.$t('energy-tariff-dialog.headline.new-tariff')
          : this.$t('energy-tariff-dialog.headline.edit-tariff')
    },
    /**
     * Returns the icon name for the dialog title based on create vs. edit mode.
     */
    titleIcon() {
      return this.isNew ? 'contract' : 'contract_edit'
    }
  },

  methods: {
    /**
     * Initializes the dialog with the selected tariff data or resets it for creating a new tariff.
     */
    init() {
      const selectedTariff = this.data?.tariffInfo?.selectedTariff
      this.additionalBlockedPeriods = this.data?.tariffInfo?.additionalBlockedPeriods ?? []

      if (selectedTariff?.id) {
        this.isNew = false
        this.tariff = {
          ...selectedTariff,
          activePeriods: selectedTariff.activePeriods ? selectedTariff.activePeriods.map(p => ({
            ...p,
            id: ++this.periodIdCounter,
            start: this.minutesToTime(p.start),
            end: this.minutesToTime(p.end),
            isEditingStart: false,
            isEditingEnd: false,
          })) : []
        }
      } else {
        this.isNew = true
        this.tariff = {tariffName: '', tariffValue: null, activePeriods: []}
      }
    },

    /**
     * Toggles the start time picker for the given period, closing the end time picker if open.
     */
    toggleStartTime(period) {
      const isOpen = period.isEditingStart
      this.$set(period, 'isEditingStart', !isOpen)
      if (!isOpen) this.$set(period, 'isEditingEnd', false)
    },

    /**
     * Toggles the end time picker for the given period, closing the start time picker if open.
     */
    toggleEndTime(period) {
      const isOpen = period.isEditingEnd
      this.$set(period, 'isEditingEnd', !isOpen)
      if (!isOpen) this.$set(period, 'isEditingStart', false)
    },

    /**
     * Adds a new active period with the given start/end minutes, defaulting to the full day.
     */
    addPeriod(start = 0, end = 1440) {
      this.tariff.activePeriods.push({
        id: ++this.periodIdCounter,
        start: this.minutesToTime(start),
        end: this.minutesToTime(end),
        isEditingStart: false,
        isEditingEnd: false,
      })
    },

    /**
     * Removes the active period at the given index and cleans up its edit state.
     */
    deletePeriod(index) {
      this.tariff.activePeriods.splice(index, 1)
    },

    /**
     * Returns true if any period has a start time that is not before its end time.
     */
    hasInvalidPeriods() {
      return this.tariff.activePeriods.some(p => this.timeToMinutes(p.start) >= this.timeToMinutes(p.end))
    },

    /**
     * Returns true if any two periods within this tariff overlap each other.
     * Expects periods sorted by start time (guaranteed by sortActivePeriods),
     * allowing an O(n) single-pass check against adjacent pairs.
     *
     * @param {{ start: number, end: number }[]} periods - Periods in minutes, sorted ascending
     * @returns {boolean}
     */
    hasSelfOverlappingPeriods(periods) {
      for (let i = 1; i < periods.length; i++) {
        if (periods[i].start < periods[i - 1].end) return true
      }
      return false
    },

    /**
     * Returns true if any period of this tariff overlaps with a period from another tariff.
     *
     * @param {{ start: number, end: number }[]} periods - Periods in minutes
     * @returns {boolean}
     */
    hasExternalOverlappingPeriods(periods) {
      for (const period of periods) {
        for (const blocked of (this.additionalBlockedPeriods || [])) {
          if (period.start < blocked.end && blocked.start < period.end) {
            return true
          }
        }
      }
      return false
    },

    /**
     * Converts minutes since midnight to a human-readable time string.
     * @param {number} minutes - Minutes since midnight (0–1440)
     * @returns {string} Time in "H:MM" format, e.g. 15 → "0:15", 255 → "4:15"
     */
    minutesToTime(minutes) {
      const h = Math.floor(minutes / 60)
      const m = minutes % 60
      return `${h}:${String(m).padStart(2, '0')}`
    },

    /**
     * Converts a time string to minutes since midnight.
     * @param {string} time - Time in "H:MM" format, e.g. "0:15", "4:15"
     * @returns {number} Minutes since midnight, e.g. "0:15" → 15, "4:15" → 255
     */
    timeToMinutes(time) {
      if (typeof time === 'number') return time
      const [h, m] = time.split(':').map(Number)
      return h * 60 + m
    },

    /**
     * Validates and saves the tariff via API; shows error toasts for invalid or overlapping periods.
     */
    saveTariff() {
      if (this.hasInvalidPeriods()) {
        this.$root.bisatoast.error({message: this.$t('energy-tariff-dialog.error.invalid-periods'), showCloseBtn: true})
        return
      }
      
      // Strips UI-only fields (id, isEditingStart, isEditingEnd) and converts times to minutes.
      const payloadPeriods = this.tariff.activePeriods.map(
          // eslint-disable-next-line no-unused-vars
          ({id, isEditingStart, isEditingEnd, ...rest}) => ({
            ...rest,
            start: this.timeToMinutes(rest.start),
            end: this.timeToMinutes(rest.end),
          })
      )

      if (this.hasSelfOverlappingPeriods(payloadPeriods)) {
        this.$root.bisatoast.error({
          message: this.$t('energy-tariff-dialog.error.overlapping-periods'),
          showCloseBtn: true
        })
        return
      }

      if (this.hasExternalOverlappingPeriods(payloadPeriods)) {
        this.$root.bisatoast.error({
          message: this.$t('energy-tariff-dialog.error.overlapping-tariffs'),
          showCloseBtn: true
        })
        return
      }

      this.$rhRequest.sendPost({
        endpoint: 'energy-tariffs/save',
        data: {...this.tariff, activePeriods: payloadPeriods}
      }, () => {
        this.$root.bisatoast.success({
          message: this.$t('energy-tariff-dialog.save-tariff.success', {name: this.tariff.tariffName}),
          showCloseBtn: true
        })
        this.$root.$emit('energyTariffsChanged', this.uncoveredTimePeriods)
        this.close()
      }, (error) => {
        console.error(error)
        this.$root.bisatoast.error({message: this.$t('app.generic-error'), showCloseBtn: true})
      })
    },

    /**
     * Deletes the current tariff via API and closes the dialog on success.
     */
    deleteTariff() {
      this.$rhRequest.sendDelete({
        endpoint: 'energy-tariffs/delete?id=' + this.tariff.id
      }, () => {
        this.$root.bisatoast.success({
          message: this.$t('energy-tariff-dialog.delete-tariff.success', {name: this.tariff.tariffName}),
          showCloseBtn: true
        })
        this.$root.$emit('energyTariffsChanged', this.getUncoveredPeriodsAfterDelete())
        this.close()
      }, (error) => {
        console.error(error)
        this.$root.bisatoast.error({message: this.$t('app.generic-error'), showCloseBtn: true})
      })
    },

    /**
     * Returns uncovered time periods after deleting the current tariff.
     * Computes coverage from other tariffs only (additionalBlockedPeriods),
     * since the deleted tariff no longer contributes any coverage.
     *
     * @returns {{ start: number, end: number }[]}
     */
    getUncoveredPeriodsAfterDelete() {
      return this.calculateUncoveredPeriods(this.additionalBlockedPeriods || [])
    },

    /**
     * Finds all time gaps within a 24-hour day (0–1440 minutes) not covered by any of the given periods.
     *
     * @param {{ start: number, end: number }[]} blockedPeriods - Periods in minutes (any order)
     * @returns {{ start: number, end: number }[]}
     */
    calculateUncoveredPeriods(blockedPeriods) {
      const DAY_END = 1440
      const sorted = blockedPeriods.slice().sort((a, b) => a.start - b.start)
      const uncovered = []
      let cursor = 0
      for (const period of sorted) {
        if (period.start > cursor) {
          uncovered.push({start: cursor, end: period.start})
        }
        if (period.end > cursor) {
          cursor = period.end
        }
      }
      if (cursor < DAY_END) {
        uncovered.push({start: cursor, end: DAY_END})
      }
      return uncovered
    },

    /**
     * Sorts activePeriods by start time ascending and keeps the edit state arrays in sync.
     */
    sortActivePeriods() {
      const periods = this.tariff.activePeriods
      if (periods.length < 2) return

      const sorted = periods.slice().sort((a, b) =>
          this.timeToMinutes(a.start) - this.timeToMinutes(b.start)
      )

      if (sorted.every((p, i) => p === periods[i])) return

      // Edit state travels with each period object — no separate array sync needed.
      this.tariff.activePeriods = sorted
    },

    /**
     * Closes the energy tariff dialog.
     */
    close() {
      this.$root.bisadialog.toggle('energyTariff')
    }
  }
}
</script>